/* Grand scroll grow: progressive enhancement; content is never hidden in CSS. */
(() => {
  'use strict';
  if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const visited = new WeakSet();
  const active = new Map();
  const pendingRoots = new Set();
  const waiting = new Set();
  let frame = 0;
  const selectors = [
    '.grand-hero__eyebrow', '.grand-hero h1', '.grand-hero__description', '.grand-hero__actions',
    '.grand-heading', '.grand-about__main', '.grand-about__bottom', '.grand-about__copy',
    '.grand-property', '.grand-project', '.grand-service-feature', '.grand-service-card',
    '.grand-why__intro', '.grand-step', '.grand-lifestyle__story', '.grand-stat',
    '.grand-lifestyle__photo', '.grand-quote', '.grand-plan', '.grand-faq__aside',
    '.grand-accordion', '.grand-person', '.grand-catalog-card', '.grand-collection-card',
    '.grand-media-gallery__item', '.grand-video-story__content',
    '.union-hub__heading', '.union-agenda__photo', '.union-agenda__event', '.union-place', '.union-podcast__copy', '.union-podcast__media',
    '.grand-footer__top > *', '.union-editorial__rule', '.union-editorial__chapter',
    '.union-study__heading', '.union-study__tier', '.union-study__program', '.union-study__cover',
    '.union-study__journey > *', '.ubn__heading', '.ubn__card', '.ur-card', '.ur-heading',
    '.union-information__heading', '.union-panel', '.union-question', '.union-intro__copy', '.union-intro__media',
    '.ud-collection__intro', '.ud-object-card', '.ud-detail__gallery', '.ud-detail__copy',
    '.uvs-intro', '.uvs-card', '.uvs-detail-art', '.uvs-detail-copy', '.uvs-next',
    'main h1', 'main h2', 'main .rte', 'main .product-grid > li',
    'main .collection-list > li', 'main .blog-articles > *', 'main .article-template__hero-container',
    'main .product-media-container'
  ].join(',');
  const excluded = 'dialog,[role="dialog"],cart-drawer,quick-add-dialog,#header-group,.grand-header,header[role="banner"],nav,form,.grand-logo-carousel,.grand-section-background,.grand-hero__slides,[data-business-reveal],details:not([open]),[hidden],[aria-hidden="true"]';
  function finish(element) {
    active.get(element)?.cancel();
    active.delete(element);
  }
  const observer = new IntersectionObserver(entries => {
    let order = 0;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const element = entry.target;
      observer.unobserve(element);
      waiting.delete(element);
      element.dataset.grandReveal = 'shown';
      if (reduced.matches || !element.isConnected || element.contains(document.activeElement)) continue;
      try {
        const mobile = matchMedia('(max-width: 760px)').matches;
        const photo = element.matches('.grand-lifestyle__photo,.grand-about__main,.union-study__cover,.grand-media-gallery__item,.union-intro__media,.union-agenda__photo,.union-podcast__media');
        const title = element.matches('.grand-heading,.union-study__heading,.ubn__heading,h1,h2,.grand-hero h1');
        const journey = element.matches('.union-study__journey > li');
        const keyframes = journey ? [
          { opacity: 0, filter: 'blur(8px)', translate: mobile ? '0 16px' : '0 30px', scale: .985 },
          { opacity: 1, filter: 'blur(0)', translate: '0 0', scale: 1 }
        ] : photo ? [
          { opacity: .3, clipPath: 'inset(8% 0 0 0)', translate: '0 16px' },
          { opacity: 1, clipPath: 'inset(0 0 0 0)', translate: '0 0' }
        ] : [
          { opacity: 0, translate: mobile ? '0 14px' : (title ? '0 28px' : '0 22px') },
          { opacity: 1, translate: '0 0' }
        ];
        const animation = element.animate(keyframes, {
          duration: journey ? 600 : (mobile ? 650 : (photo ? 1150 : (title ? 1000 : 850))),
          delay: journey ? (mobile ? 0 : Number(element.style.getPropertyValue('--journey-order')) * 620) : Math.min(order++ * 85, 255),
          easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards'
        });
        animation.id = 'grand-scroll-grow';
        active.set(element, animation);
        animation.onfinish = animation.oncancel = () => active.delete(element);
      } catch { /* Unsupported animation features must never hide content. */ }
    }
  }, { threshold: .06, rootMargin: '0px 0px -24px 0px' });
  function scan(root) {
    if (reduced.matches || !root?.querySelectorAll) return;
    const elements = [...(root.matches?.(selectors) ? [root] : []), ...root.querySelectorAll(selectors)];
    for (const element of elements) {
      if (visited.has(element) || element.closest(excluded) || element.parentElement?.closest(selectors)) continue;
      visited.add(element);
      element.dataset.grandReveal = 'ready';
      observer.observe(element);
      waiting.add(element);
    }
  }
  function revealFocused(event) {
    for (const [element] of active) if (element.contains(event.target)) finish(element);
    const target = event.target.closest?.('[data-grand-reveal]');
    if (target) { observer.unobserve(target); waiting.delete(target); target.dataset.grandReveal = 'shown'; }
  }
  function start() {
    scan(document);
    new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) pendingRoots.add(node);
      if (!pendingRoots.size || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        for (const root of pendingRoots) if (root.isConnected) scan(root);
        pendingRoots.clear();
      });
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('focusin', revealFocused);
    document.addEventListener('shopify:section:load', event => scan(event.target));
    document.addEventListener('shopify:section:unload', event => {
      for (const element of waiting) if (event.target.contains(element)) { observer.unobserve(element); waiting.delete(element); }
      for (const [element] of active) if (event.target.contains(element)) finish(element);
    });
    for (const name of ['shopify:section:select', 'shopify:block:select']) {
      document.addEventListener(name, event => {
        for (const element of event.target.querySelectorAll('[data-grand-reveal]')) {
          observer.unobserve(element); waiting.delete(element); finish(element); element.dataset.grandReveal = 'shown';
        }
        revealFocused(event);
      });
    }
  }
  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      observer.disconnect();
      for (const element of waiting) element.dataset.grandReveal = 'shown';
      waiting.clear();
      for (const [element] of active) finish(element);
    } else scan(document);
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) for (const [element] of active) finish(element);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

(() => {
  'use strict';
  const controllers = new WeakMap();
  const ambientControllers = new WeakMap();
  const stickyHeader = document.querySelector('#header-group');
  const measureSticky = () => document.documentElement.style.setProperty('--union-sticky-height', `${stickyHeader?.offsetHeight || 100}px`);
  measureSticky();
  if (stickyHeader && 'ResizeObserver' in window) new ResizeObserver(measureSticky).observe(stickyHeader);
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('data-business-open')) return;
    const url = new URL(link.href, location.href);
    if (!url.hash || url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return;
    let target;
    try { target = document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch { return; }
    if (!target || target.matches('dialog')) return;
    event.preventDefault();
    document.querySelector('.grand-mobile-menu[open]')?.removeAttribute('open');
    const wrapper = document.querySelector('.page-wrapper');
    const scrollable = wrapper && wrapper.scrollHeight > wrapper.clientHeight && ['auto','scroll'].includes(getComputedStyle(wrapper).overflowY);
    const offset = (stickyHeader?.offsetHeight || 0) + 18;
    const top = scrollable ? wrapper.scrollTop + target.getBoundingClientRect().top - wrapper.getBoundingClientRect().top - offset : window.scrollY + target.getBoundingClientRect().top - offset;
    (scrollable ? wrapper : window).scrollTo({top:Math.max(0,top),behavior:'instant'});
    if (location.hash !== url.hash) history.pushState(null,'',url.hash);
  });
  function initAmbient(video) {
    ambientControllers.get(video)?.abort();
    const controller = new AbortController();
    ambientControllers.set(video, controller);
    const { signal } = controller;
    const section = video.closest('section');
    const button = section?.querySelector('[data-grand-ambient-toggle]');
    const reduce = matchMedia('(prefers-reduced-motion: reduce)');
    let paused = reduce.matches, visible = false;
    const ui = () => {
      if (!button) return;
      button.textContent = paused ? '▶' : 'Ⅱ';
      button.setAttribute('aria-label', paused ? 'Play background' : 'Pause background');
    };
    const sync = () => {
      if (paused || !visible || document.hidden) video.pause();
      else {
        video.muted = true; video.playsInline = true;
        video.play().catch(() => { paused = true; ui(); });
      }
      ui();
    };
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, { threshold: .05 });
    observer.observe(video);
    button?.addEventListener('click', () => { paused = !paused; sync(); }, { signal });
    document.addEventListener('visibilitychange', sync, { signal });
    reduce.addEventListener('change', () => { paused = reduce.matches; sync(); }, { signal });
    signal.addEventListener('abort', () => { video.pause(); observer.disconnect(); }, { once: true });
    ui();
  }
  function initHero(hero) {
    controllers.get(hero)?.abort();
    const controller = new AbortController();
    controllers.set(hero, controller);
    const { signal } = controller;
    const slides = [...hero.querySelectorAll('[data-grand-slide]')];
    const carousel = hero.querySelector('[data-carousel]')?.dataset.carousel !== 'false';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width:760px)');
    let index = 0, timer, fadeTimer, sequence = 0, pendingIndex = -1;
    let userPaused = reduce.matches, inView = true;
    const failed = new WeakSet();
    const videos = () => [...hero.querySelectorAll('video')];
    const videoAt = currentIndex => {
      const current = slides[currentIndex];
      if (!current) return null;
      return current.querySelector(mobile.matches && current.classList.contains('has-mobile') ? '.grand-video--mobile' : '.grand-video--desktop');
    };
    const activeVideo = () => videoAt(index);
    const allowed = () => !userPaused && !document.hidden && inView && !signal.aborted;
    const stop = () => { clearTimeout(timer); videos().forEach(v => v.pause()); };
    const schedule = () => {
      clearTimeout(timer);
      if (carousel && slides.length > 1 && allowed()) timer = setTimeout(advance, Number(hero.dataset.duration) || 8000);
    };
    const preloadNext = () => {
      if (!carousel || slides.length < 2 || !allowed()) return;
      const video = videoAt((index + 1) % slides.length);
      if (video && !failed.has(video) && video.preload === 'none') {
        video.preload = 'metadata';
        video.load();
      }
    };
    const play = () => {
      stop();
      if (!allowed()) return;
      const video = activeVideo();
      if (video && !failed.has(video)) {
        video.muted = true; video.volume = 0; video.controls = false; video.playsInline = true;
        video.loop = !carousel || slides.length === 1;
        video.play().catch(() => {
          if (video !== activeVideo() || !allowed()) return;
          userPaused = true; 
        });
      }
      schedule();
      preloadNext();
    };
    const ready = video => new Promise((resolve, reject) => {
      if (video.readyState >= 2) { resolve(); return; }
      let timeout;
      const cleanup = () => {
        clearTimeout(timeout);
        video.removeEventListener('loadeddata', success);
        video.removeEventListener('error', error);
        signal.removeEventListener('abort', abort);
      };
      const success = () => { cleanup(); resolve(); };
      const error = () => { cleanup(); reject(new Error('Video unavailable')); };
      const abort = () => { cleanup(); reject(new Error('Hero unloaded')); };
      video.addEventListener('loadeddata', success, { once: true });
      video.addEventListener('error', error, { once: true });
      signal.addEventListener('abort', abort, { once: true });
      timeout = setTimeout(error, 15000);
      video.preload = 'auto';
      video.load();
    });
    function advance() {
      if (!allowed() || !carousel || slides.length < 2) return;
      for (let offset = 1; offset <= slides.length; offset++) {
        const next = (index + offset) % slides.length;
        const video = videoAt(next);
        if (!video || !failed.has(video)) { show(next); return; }
      }
      userPaused = true; 
    }
    async function show(next) {
      if (!slides.length) return;
      const target = Math.max(0, Math.min(next, slides.length - 1));
      const attempt = ++sequence;
      pendingIndex = target;
      clearTimeout(timer);
      const oldIndex = index;
      const video = videoAt(target);
      if (video) {
        try {
          if (failed.has(video)) throw new Error('Video unavailable');
          await ready(video);
          if (attempt !== sequence || signal.aborted) return;
          video.currentTime = 0;
          video.muted = true; video.volume = 0; video.controls = false;
          video.playsInline = true;
          video.loop = !carousel || slides.length === 1;
          if (allowed()) {
            try { await video.play(); }
            catch { userPaused = true;  }
          }
        } catch {
          if (signal.aborted || attempt !== sequence) return;
          failed.add(video);
          pendingIndex = -1;
          if (carousel && allowed()) {
            for (let offset = 1; offset <= slides.length; offset++) {
              const candidate = (target + offset) % slides.length;
              const candidateVideo = videoAt(candidate);
              if (!candidateVideo || !failed.has(candidateVideo)) { show(candidate); return; }
            }
          }
          userPaused = true;  return;
        }
      }
      if (attempt !== sequence || signal.aborted) {
        if (video && video !== activeVideo()) video.pause();
        return;
      }
      index = target;
      pendingIndex = -1;
      clearTimeout(fadeTimer);
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.classList.toggle('is-leaving', i === oldIndex && oldIndex !== index);
        slide.setAttribute('aria-hidden', String(i !== index));
      });
      videos().forEach(other => {
        if (other !== video) other.muted = true;
        if (other !== video && !other.closest('.is-leaving')) other.pause();
      });
      if (!allowed()) video?.pause();
      schedule();
      fadeTimer = setTimeout(() => {
        slides.forEach(slide => slide.classList.remove('is-leaving'));
        videos().forEach(other => { if (other !== activeVideo()) other.pause(); });
      }, reduce.matches ? 0 : 900);
       preloadNext();
    }
    videos().forEach(video => {
      video.muted = true; video.defaultMuted = true; video.volume = 0; video.controls = false; video.tabIndex = -1;
      video.setAttribute("disablepictureinpicture", "");
      video.setAttribute("disableremoteplayback", "");
      video.addEventListener('ended', () => { if (video === activeVideo() && pendingIndex < 0) advance(); }, { signal });
      video.addEventListener('error', () => {
        failed.add(video);
        if (video === activeVideo() && pendingIndex < 0 && allowed()) schedule();
      }, { signal });
    });
    document.addEventListener('visibilitychange', play, { signal });
    mobile.addEventListener('change', () => { stop(); show(index); }, { signal });
    reduce.addEventListener('change', () => { userPaused = reduce.matches; play();  }, { signal });
    hero.addEventListener('shopify:block:select', event => { const selected = slides.findIndex(s => s.dataset.blockId === event.detail.blockId); if (selected >= 0) { userPaused = true; show(selected); } }, { signal });
    const observer = new IntersectionObserver(entries => { inView = entries[0].isIntersecting; play(); }, { threshold:.05 });
    observer.observe(hero);
    signal.addEventListener('abort', () => { sequence++; clearTimeout(fadeTimer); stop(); observer.disconnect(); }, { once:true });
     show(0);
  }
  function init(root = document) {
    root.querySelectorAll('[data-grand-hero]').forEach(initHero);
    root.querySelectorAll('.grand-ambient-video').forEach(initAmbient);
  }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-grand-hero]').forEach(hero => controllers.get(hero)?.abort()));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.grand-ambient-video').forEach(video => ambientControllers.get(video)?.abort()));
  document.addEventListener('shopify:block:select', event => {
    const details = event.target.closest('.grand-accordion details') || event.target.querySelector('.grand-accordion details');
    if (details) details.open = true;
  });
  document.addEventListener('click', event => {
    const menu = document.querySelector('.grand-mobile-menu[open]');
    if (menu && (!menu.contains(event.target) || event.target.closest('a'))) menu.open = false;
    document.querySelectorAll('.grand-menu-dropdown[open]').forEach(dropdown => { if (!dropdown.contains(event.target) || event.target.closest('a')) dropdown.open = false; });
    const close = event.target.closest('[data-grand-close]');
    if (close) close.closest('dialog')?.close();
    const plan = event.target.closest('[data-grand-plan]');
    if (plan && plan.hash === '#contact' && plan.origin === location.origin) {
      const dialog = document.querySelector('[data-grand-enquiry]');
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        const title = plan.dataset.grandPlan;
        const price = plan.closest('.grand-plan')?.querySelector('.grand-plan__price strong')?.textContent.trim();
        dialog.querySelector('[data-grand-plan-field]').value = title + (price ? ' — ' + price : '');
        dialog.showModal();
        dialog.querySelector('#GrandName')?.focus();
      }
    }
    if (event.target.matches('dialog[data-grand-enquiry]')) {
      const box = event.target.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) event.target.close();
    }
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') document.querySelectorAll('.grand-mobile-menu[open]').forEach(menu => menu.open = false);
    if (event.key === 'Escape') document.querySelectorAll('.grand-menu-dropdown[open]').forEach(menu => menu.open = false);
  });
  if (document.querySelector('[data-grand-form-success], [data-grand-form-error]')) document.querySelector('[data-grand-enquiry]')?.showModal();
})();
